import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { Play, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface HeadlineABConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variations: HeadlineOverlayVariation[];
  distribution: number[];
}

export function HeadlineABConfirmModal({
  open,
  onClose,
  onConfirm,
  variations,
  distribution
}: HeadlineABConfirmModalProps) {
  useLanguage();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ab_test', 'edit');

  useEffect(() => {
    if (open && !canEdit) {
      toast.error(translateInterfaceText("Sem permissão para criar testes A/B"));
      onClose();
    }
  }, [open, canEdit, onClose]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Play className="h-5 w-5 text-primary" />
            {translateInterfaceText("Iniciar Teste A/B")}</DialogTitle>
          <DialogDescription>
            {translateInterfaceText("Confirme as variações e distribuição de tráfego antes de iniciar o teste.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {variations.map((variation, index) => (
            <div 
              key={variation.id}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Type className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {variation.name || translateInterfaceText("Variação {value0}", { value0: (String.fromCharCode(65 + index)) })}
                  </p>
                  {variation.headline?.text && (
                    <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                      {variation.headline.text}
                    </p>
                  )}
                </div>
              </div>
              <span className="text-sm font-semibold text-primary tabular-nums">
                {distribution[index] ?? 0}%
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            {translateInterfaceText("Cancelar")}</Button>
          <Button onClick={onConfirm} className="gap-2">
            <Play className="h-4 w-4" />
            {translateInterfaceText("Iniciar Teste")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
